import { Box, FormField, Spinner, TextInput } from 'grommet';
import { WidthType } from 'grommet/utils';
import React, { KeyboardEventHandler, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { InlineEdit } from '@/components/form-controls';
import { BidRecommendation, BidRecommendationDrugPackaging } from '@/features/bid-analysis/types/bid-recommendation';

import * as BidRecommendationFunctions from './bid-recommendation.functions';
import { BidRecommendationPackagingFormInputs } from './bid-recommendation-packaging-form-inputs';

type DescriptionCellProps = {
  bidRecommendation: BidRecommendation;
  packaging: BidRecommendationDrugPackaging;
  editingFieldId?: string;
  savingFieldId?: string;
  onFocus: (fieldId: string) => void;
  onCancel: () => void;
  onSave: (
    bidRecommendation: BidRecommendation,
    row: BidRecommendationDrugPackaging,
    fieldId: string,
    form: BidRecommendationPackagingFormInputs) => void;
  cellWidth?: WidthType
};

export const DescriptionCell = (props: DescriptionCellProps) => {
  const {
    bidRecommendation,
    packaging,
    editingFieldId,
    savingFieldId,
    onFocus,
    onCancel,
    onSave,
    cellWidth = '8rem'
  } = props;

  const id = BidRecommendationFunctions.buildFieldId({
    bidRecommendationId: bidRecommendation.id,
    isHistorical: bidRecommendation.isHistorical,
    packagingId: packaging.id,
    fieldName: 'description'
  });

  const editing = editingFieldId === id;
  const saving = savingFieldId === id;

  const {
    control,
    getValues,
    formState: { errors, isDirty  },
    reset
  } = useForm<BidRecommendationPackagingFormInputs>({ mode: 'onChange', reValidateMode: 'onChange', defaultValues: { description: packaging.description } });

  useEffect(() => {
    reset({ description: packaging.description });
  }, [packaging.description]);

  const handleFocus = () => {
    onFocus(id);
  };

  const handleSave = async (form: BidRecommendationPackagingFormInputs) => {
    if (isDirty) {
      onSave(bidRecommendation, packaging, id, { ...form });
    } else {
      onCancel();
      reset();
    }
  };

  const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = (event) => {
    if (event.key === 'Enter') {
      handleSave(getValues());
    }

    if (event.key === 'Escape') {
      reset();
      onCancel();
    }
  };

  return (
    <Box width={cellWidth} fill="vertical">
      {!bidRecommendation.isHistorical
        && <span>{packaging.description}</span>}

      {bidRecommendation.isHistorical
        && <Controller
          control={control}
          name="description"
          rules={{
            maxLength: 11,
            minLength: 11
          }}
          render={({ field: { value, onChange } }) => (
            <InlineEdit
              editing={editing}
              onFocus={handleFocus}
              readonlyComponent={<span>{packaging.description ?? <>&mdash;</>}</span>}
              inputComponent={
                <FormField
                  name="description"
                  htmlFor={id}
                  error={errors.supplementalRebate?.message}
                >
                  <TextInput
                    id={id}
                    value={value}
                    onChange={onChange}
                    onBlur={() => handleSave(getValues())}
                    onKeyDown={handleKeyDown}
                    name="description"
                    icon={saving ? <Spinner size="xsmall" color="brand"/> : undefined}
                    maxLength={100}
                    reverse
                  />
                </FormField>
              }
            />
          )}
        />
      }
    </Box>
  );
};
